import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Card,
  CardContent,
  Divider,
  IconButton,
  List,
  ListItemText,
  ListItemSecondaryAction,
  ListItem
} from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import {
  prescriptionsService,
  CreatePrescriptionData,
  Medication,
  PrescriptionStatus
} from '@services/prescriptionsService';
import { useAuth } from '@contexts/AuthContext';

interface IssuePrescriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  petId: string;
  ownerId: string;
  petName: string;
}

interface PrescriptionMedication {
  id: string;
  medicationId: string;
  medicationName: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
  quantity: number;
  unit: string;
}

const IssuePrescriptionDialog: React.FC<IssuePrescriptionDialogProps> = ({
  open,
  onClose,
  onSuccess,
  petId,
  ownerId,
  petName
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allergyWarning, setAllergyWarning] = useState<string | null>(null);
  const [showAllergyConfirmation, setShowAllergyConfirmation] = useState(false);
  
  const [formData, setFormData] = useState({
    reason: '',
    notes: '',
    visitId: '', // Optional - if issued during a visit
    status: 'draft' as PrescriptionStatus
  });

  const [medications, setMedications] = useState<PrescriptionMedication[]>([]);
  const [availableMedications, setAvailableMedications] = useState<Medication[]>([]);
  const [newMedication, setNewMedication] = useState<Partial<PrescriptionMedication>>({
    medicationId: '',
    medicationName: '',
    dosage: '',
    frequency: 'O data pe zi',
    duration: '7 zile',
    instructions: '',
    quantity: 1,
    unit: 'cutie'
  });

  const frequencyOptions = [
    'O data pe zi',
    'De doua ori pe zi',
    'De trei ori pe zi',
    'In fiecare 8 ore',
    'In fiecare 12 ore',
    'La nevoie',
    'Inainte de masa',
    'Dupa masa',
    'Seara inainte de culcare'
  ];

  const unitOptions = [
    'cutie',
    'cutii',
    'comprimate',
    'capsule',
    'ml',
    'flacoane',
    'tuburi',
    'doze'
  ];

  useEffect(() => {
    if (open) {
      loadMedications();
      checkAllergies();
    }
  }, [open, petId]);

  const loadMedications = async () => {
    try {
      if (!user?.clinicId) return;
      
      const medicationsData = await prescriptionsService.getMedications();
      setAvailableMedications(medicationsData);
    } catch (error) {
      console.error('Error loading medications:', error);
      setError('Eroare la incarcarea medicamentelor disponibile');
    }
  };

  const checkAllergies = async () => {
    try {
      // Check if pet has any allergies that might conflict with common medications
      const allergies = await prescriptionsService.getPetAllergies(petId);
      if (allergies.length > 0) {
        setAllergyWarning(`ATENTIE: Animalul are alergii cunoscute la: ${allergies.join(', ')}`);
      }
    } catch (error) {
      console.error('Error checking allergies:', error);
    }
  };

  const handleAddMedication = () => {
    if (!newMedication.medicationId || !newMedication.dosage || !newMedication.quantity) {
      setError('Completati toate campurile obligatorii pentru medicament');
      return;
    }

    const medication: PrescriptionMedication = {
      id: Date.now().toString(),
      medicationId: newMedication.medicationId!,
      medicationName: newMedication.medicationName!,
      dosage: newMedication.dosage!,
      frequency: newMedication.frequency!,
      duration: newMedication.duration!,
      instructions: newMedication.instructions || '',
      quantity: newMedication.quantity!,
      unit: newMedication.unit!
    };

    setMedications(prev => [...prev, medication]);
    
    // Reset form
    setNewMedication({
      medicationId: '',
      medicationName: '',
      dosage: '',
      frequency: 'O data pe zi',
      duration: '7 zile',
      instructions: '',
      quantity: 1,
      unit: 'cutie'
    });
    setError(null);
  };

  const handleRemoveMedication = (id: string) => {
    setMedications(prev => prev.filter(med => med.id !== id));
  };

  const handleMedicationSelect = (medicationId: string) => {
    const selectedMed = availableMedications.find(med => med.id === medicationId);
    if (selectedMed) {
      setNewMedication(prev => ({
        ...prev,
        medicationId: selectedMed.id,
        medicationName: selectedMed.name
      }));

      // Check for allergy conflicts
      checkMedicationAllergies(selectedMed.name);
    }
  };

  const checkMedicationAllergies = async (medicationName: string) => {
    try {
      const hasConflict = await prescriptionsService.checkAllergyConflict(
        petId,
        medicationName,
        newMedication.medicationId
      );
      if (hasConflict) {
        setAllergyWarning(`ATENTIE: Animalul poate avea alergii la ${medicationName}!`);
        setShowAllergyConfirmation(true);
      }
    } catch (error) {
      console.error('Error checking medication allergies:', error);
    }
  };

  const handleSaveDraft = async () => {
    if (!user?.clinicId || medications.length === 0) {
      setError('Adaugati cel putin un medicament');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const prescriptionData: CreatePrescriptionData = {
        clinicId: user.clinicId,
        petId,
        ownerId,
        veterinarianId: user.id,
        visitId: formData.visitId || undefined,
        reason: formData.reason,
        notes: formData.notes,
        medications: medications.map(med => ({
          medicationId: med.medicationId,
          medicationName: med.medicationName,
          dosage: med.dosage,
          frequency: med.frequency,
          duration: med.duration,
          instructions: med.instructions,
          quantity: med.quantity,
          unit: med.unit
        })),
        status: 'draft'
      };

      await prescriptionsService.createPrescription(prescriptionData);
      
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error saving prescription draft:', error);
      setError('Eroare la salvarea draft-ului retetei');
    } finally {
      setLoading(false);
    }
  };

  const handleIssuePrescription = async () => {
    if (!user?.clinicId || medications.length === 0) {
      setError('Adaugati cel putin un medicament');
      return;
    }

    if (showAllergyConfirmation) {
      // Show confirmation dialog for allergy conflicts
      if (!window.confirm(`${allergyWarning}\n\nSigur doriti sa continuati cu aceasta reteta?`)) {
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const prescriptionData: CreatePrescriptionData = {
        clinicId: user.clinicId,
        petId,
        ownerId,
        veterinarianId: user.id,
        visitId: formData.visitId || undefined,
        reason: formData.reason,
        notes: formData.notes,
        medications: medications.map(med => ({
          medicationId: med.medicationId,
          medicationName: med.medicationName,
          dosage: med.dosage,
          frequency: med.frequency,
          duration: med.duration,
          instructions: med.instructions,
          quantity: med.quantity,
          unit: med.unit
        })),
        status: 'active'
      };

      const prescription = await prescriptionsService.createPrescription(prescriptionData);
      
      // Generate and download PDF
      await prescriptionsService.generatePrescriptionPDF(prescription.id);
      
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error issuing prescription:', error);
      setError('Eroare la emiterea retetei');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      reason: '',
      notes: '',
      visitId: '',
      status: 'draft'
    });
    setMedications([]);
    setNewMedication({
      medicationId: '',
      medicationName: '',
      dosage: '',
      frequency: 'O data pe zi',
      duration: '7 zile',
      instructions: '',
      quantity: 1,
      unit: 'cutie'
    });
    setError(null);
    setAllergyWarning(null);
    setShowAllergyConfirmation(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PrintIcon />
          <Typography variant="h6">
            Emite Reteta pentru {petName}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {allergyWarning && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            icon={<WarningIcon />}
          >
            {allergyWarning}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Reason and Notes */}
          <Grid size={12}>
            <TextField
              label="Motiv reteta"
              fullWidth
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="ex: Tratament antibiotic post-operator"
              helperText="Motivul pentru care se emite aceasta reteta"
            />
          </Grid>

          <Grid size={12}>
            <TextField
              label="Note suplimentare"
              fullWidth
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Note pentru proprietar sau farmacist"
            />
          </Grid>

          {/* Add Medication Section */}
          <Grid size={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Adauga Medicament
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Medicament</InputLabel>
                      <Select
                        value={newMedication.medicationId}
                        onChange={(e) => handleMedicationSelect(e.target.value)}
                        label="Medicament"
                      >
                        {availableMedications.map((med) => (
                          <MenuItem key={med.id} value={med.id}>
                            {med.name} - {med.concentration}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Dozaj"
                      fullWidth
                      value={newMedication.dosage}
                      onChange={(e) => setNewMedication(prev => ({ ...prev, dosage: e.target.value }))}
                      placeholder="ex: 1 comprimat, 0.5ml/kg"
                      helperText="Doza per administrare"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Frecventa</InputLabel>
                      <Select
                        value={newMedication.frequency}
                        onChange={(e) => setNewMedication(prev => ({ ...prev, frequency: e.target.value }))}
                        label="Frecventa"
                      >
                        {frequencyOptions.map((freq) => (
                          <MenuItem key={freq} value={freq}>
                            {freq}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Durata"
                      fullWidth
                      value={newMedication.duration}
                      onChange={(e) => setNewMedication(prev => ({ ...prev, duration: e.target.value }))}
                      placeholder="ex: 7 zile, 2 saptamani"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box display="flex" gap={1}>
                      <TextField
                        label="Cantitate"
                        type="number"
                        value={newMedication.quantity}
                        onChange={(e) => setNewMedication(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                        sx={{ width: '60%' }}
                      />
                      <FormControl sx={{ width: '40%' }}>
                        <InputLabel>Unitate</InputLabel>
                        <Select
                          value={newMedication.unit}
                          onChange={(e) => setNewMedication(prev => ({ ...prev, unit: e.target.value }))}
                          label="Unitate"
                        >
                          {unitOptions.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid size={12}>
                    <TextField
                      label="Instructiuni speciale"
                      fullWidth
                      value={newMedication.instructions}
                      onChange={(e) => setNewMedication(prev => ({ ...prev, instructions: e.target.value }))}
                      placeholder="ex: Se administreaza cu mancare, Nu se administreaza cu lactate"
                    />
                  </Grid>

                  <Grid size={12}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddMedication}
                      disabled={!newMedication.medicationId || !newMedication.dosage}
                    >
                      Adauga Medicament
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Medications List */}
          {medications.length > 0 && (
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Medicamente Prescrise ({medications.length})
                  </Typography>
                  
                  <List>
                    {medications.map((med, index) => (
                      <React.Fragment key={med.id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight="bold">
                                {med.medicationName}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2">
                                  <strong>Dozaj:</strong> {med.dosage} | <strong>Frecventa:</strong> {med.frequency}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Durata:</strong> {med.duration} | <strong>Cantitate:</strong> {med.quantity} {med.unit}
                                </Typography>
                                {med.instructions && (
                                  <Typography variant="body2" color="primary">
                                    <strong>Instructiuni:</strong> {med.instructions}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleRemoveMedication(med.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < medications.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          Anuleaza
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSaveDraft}
          disabled={loading || medications.length === 0}
        >
          Salveaza Draft
        </Button>
        
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handleIssuePrescription}
          disabled={loading || medications.length === 0}
        >
          {loading ? <CircularProgress size={20} /> : 'Emite Reteta (PDF)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssuePrescriptionDialog;
